<script setup>
import { SENTIMENT_COLORS, SENTIMENT_SEGMENTS } from "@/lib/chartPalettes";
import { ref, computed, inject } from "vue";
import { ChevronDown, ChevronUp } from "lucide-vue-next";
import { Tooltip } from "@/components/ui/tooltip";
import BarChartTooltip from "./BarChartTooltip.vue";
import ResultsVerbatimsList from "./ResultsVerbatimsList.vue";
import AnimatedPercent from "./AnimatedPercent.vue";
import { renderMarkdownInline } from "@utils/renderMarkdown";
import { ImageLightbox } from "@/components/episto-ui/image-lightbox";

const props = defineProps({
  populations: { type: Array, required: true },
  horizontal: { type: Boolean, default: true },
  showSignificance: { type: Boolean, default: false },
  showSentiment: { type: Boolean, default: false },
  clickable: { type: Boolean, default: false },
  collapsible: { type: Boolean, default: false },
  verbatimsQuestionId: { type: Number, default: null },
  collapsibleCode: { type: String, default: null },
  collapsibleCodeVerbatimsQuestionId: { type: Number, default: null },
  showImages: { type: Boolean, default: false },
});

const emit = defineEmits(["bar-click"]);

// Bars grow from 0 when the card is revealed on scroll (view mode entrance animation)
const revealed = inject("resultsCardRevealed", ref(true));

const expandedCode = ref(null);
const previewImage = ref(null);

// With images, the label column fits the longest label (≈5.5px/char) so short labels don't leave a gap.
const LABEL_MIN_WIDTH = 48;
const IMAGE_SLOT_WIDTH = 36 + 8; // h-9 w-9 thumbnail + gap-2
const labelColumnWidth = computed(() => {
  if (!props.showImages) return null;
  const longest = Math.max(0, ...(props.populations[0]?.data || []).map((item) => String(item.label || "").length));
  const textWidth = Math.max(Math.ceil(longest * 5.5), LABEL_MIN_WIDTH);
  return `${Math.min(textWidth + IMAGE_SLOT_WIDTH, 180)}px`;
});

function isItemCollapsible(item) {
  if (props.collapsible) return true;
  return !!props.collapsibleCode && item.code === props.collapsibleCode && !!props.collapsibleCodeVerbatimsQuestionId;
}

function barCursorClass(item) {
  return (props.clickable || isItemCollapsible(item)) ? "!cursor-pointer" : "!cursor-default";
}

function verbatimsIdFor(item) {
  if (props.collapsibleCode && item.code === props.collapsibleCode) return props.collapsibleCodeVerbatimsQuestionId;
  return props.verbatimsQuestionId;
}

function handleBarClick(item, popIdx = null) {
  if (isItemCollapsible(item)) {
    expandedCode.value = expandedCode.value === item.code ? null : item.code;
  } else {
    emit("bar-click", { code: item.code, popId: popIdx == null ? null : props.populations[popIdx]?.popId });
  }
}

function onPopBarClick(event, item, popIdx) {
  if (!props.clickable || isItemCollapsible(item) || item.isSubtotal) return;
  event.stopPropagation();
  handleBarClick(item, popIdx);
}

function barWidth(item) {
  if (!revealed.value) return "0%";
  return `${Math.min(item.value * 100, 100)}%`;
}

// Bars start growing one after the other (Appinio-style stagger), capped for long lists
function revealDelay(index) {
  return Math.min(index * 40, 400);
}

function revealDelayStyle(index) {
  return { transitionDelay: `${revealDelay(index)}ms` };
}

function sigIcon(item) {
  if (item.significance === "+") return "up";
  if (item.significance === "-") return "down";
  return null;
}
</script>

<template>
  <!-- Horizontal bars -->
  <div v-if="horizontal" class="flex flex-col" :class="showImages ? 'gap-4' : 'gap-3'">
    <div
      v-for="(item, itemIdx) in populations[0]?.data || []"
      :key="item.code || itemIdx">
      <div
        class="flex items-center gap-2 transition-all duration-200"
        :class="isItemCollapsible(item) ? 'cursor-pointer' : ''"
        @click="isItemCollapsible(item) && handleBarClick(item)">
        <div class="shrink-0 flex items-center gap-2" :class="{ 'w-[160px]': !showImages }" :style="{ width: labelColumnWidth }">
          <Tooltip v-if="showImages && item.image" :delay-duration="0" trigger-class="shrink-0" content-class="bg-transparent p-0 shadow-none">
            <img
              :src="item.image"
              :alt="item.label"
              class="h-9 w-9 rounded object-cover cursor-zoom-in"
              @click.stop="previewImage = item.image" />
            <template #content>
              <img :src="item.image" :alt="item.label" class="max-h-60 max-w-60 rounded-md border bg-background object-contain shadow-lg" />
            </template>
          </Tooltip>
          <div v-else-if="showImages" class="h-9 w-9 shrink-0" />
          <Tooltip :trigger-class="`flex-1 min-w-0 ${barCursorClass(item)}`">
            <span
              class="block text-xs leading-[15px] font-medium line-clamp-2 break-words text-right"
              v-html="renderMarkdownInline(item.label)" />
            <template #content>
              <span v-html="renderMarkdownInline(item.label)" />
            </template>
          </Tooltip>
        </div>
        <div class="flex-1 min-w-0">
          <Tooltip :delay-duration="0" :trigger-class="barCursorClass(item)">
            <div class="flex flex-col gap-0.5">
              <div
                v-for="(pop, popIdx) in populations"
                :key="popIdx"
                class="flex items-center gap-1.5"
                :class="clickable && !isItemCollapsible(item) ? 'cursor-pointer' : ''"
                @click="onPopBarClick($event, item, popIdx)">
                <div class="relative flex-1 h-5">
                  <div class="absolute inset-0 rounded bg-muted/30 overflow-hidden">
                    <div
                      v-if="showSentiment && (pop.data[itemIdx] || item).sentimentBreakdown"
                      class="absolute inset-y-0 left-0 flex rounded overflow-hidden transition-all duration-300 starting:!w-0 starting:opacity-0"
                      :style="[{ width: barWidth(pop.data[itemIdx] || item) }, revealDelayStyle(itemIdx)]">
                      <div class="h-full" :style="{ width: (pop.data[itemIdx] || item).sentimentBreakdown.negative * 100 + '%', backgroundColor: SENTIMENT_COLORS.negative }" />
                      <div class="h-full" :style="{ width: (pop.data[itemIdx] || item).sentimentBreakdown.neutral * 100 + '%', backgroundColor: SENTIMENT_COLORS.neutral }" />
                      <div class="h-full" :style="{ width: (pop.data[itemIdx] || item).sentimentBreakdown.positive * 100 + '%', backgroundColor: SENTIMENT_COLORS.positive }" />
                    </div>
                    <div
                      v-else
                      class="absolute inset-y-0 left-0 rounded transition-all duration-300 starting:!w-0 starting:opacity-0"
                      :style="[{ width: barWidth(pop.data[itemIdx] || item), backgroundColor: (pop.data[itemIdx] || item).color || pop.color, opacity: (pop.data[itemIdx] || item).isSubtotal ? 0.5 : 1 }, revealDelayStyle(itemIdx)]" />
                  </div>
                  <span
                    v-if="(pop.data[itemIdx] || item).value > 0.9"
                    class="absolute top-0 h-5 leading-5 text-xs font-medium tabular-nums pr-1.5 whitespace-nowrap text-white flex items-center justify-end gap-1 transition-all duration-300"
                    :style="[{ left: 0, width: barWidth(pop.data[itemIdx] || item) }, revealDelayStyle(itemIdx)]">
                    <AnimatedPercent :value="(pop.data[itemIdx] || item).value" :delay="revealDelay(itemIdx)" />
                    <span
                      v-if="showSignificance && sigIcon(pop.data[itemIdx] || item) === 'up'"
                      class="text-3xs text-status-success"
                    >▲</span>
                    <span
                      v-if="showSignificance && sigIcon(pop.data[itemIdx] || item) === 'down'"
                      class="text-3xs text-status-error"
                    >▼</span>
                  </span>
                  <span
                    v-else
                    class="absolute top-0 h-5 leading-5 text-xs font-medium tabular-nums pl-1.5 whitespace-nowrap flex items-center gap-1 transition-all duration-300"
                    :style="[{ left: barWidth(pop.data[itemIdx] || item) }, revealDelayStyle(itemIdx)]">
                    <AnimatedPercent :value="(pop.data[itemIdx] || item).value" :delay="revealDelay(itemIdx)" />
                    <span
                      v-if="showSignificance && sigIcon(pop.data[itemIdx] || item) === 'up'"
                      class="text-3xs text-status-success-strong"
                    >▲</span>
                    <span
                      v-if="showSignificance && sigIcon(pop.data[itemIdx] || item) === 'down'"
                      class="text-3xs text-status-error-strong"
                    >▼</span>
                  </span>
                </div>
              </div>
            </div>
            <template #content>
              <BarChartTooltip
                :item="item"
                :item-idx="itemIdx"
                :populations="populations"
                :show-significance="showSignificance"
                :show-sentiment="showSentiment"
                :clickable="isItemCollapsible(item)"
                @bar-click="handleBarClick($event)"
              />
            </template>
          </Tooltip>
        </div>
        <div class="shrink-0 w-5 h-5 flex items-center justify-center print:hidden">
          <button
            v-if="isItemCollapsible(item)"
            class="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
            @click.stop="handleBarClick(item)">
            <ChevronUp v-if="expandedCode === item.code" class="h-3.5 w-3.5" />
            <ChevronDown v-else class="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <!-- Inline verbatims collapse -->
      <div
        v-if="isItemCollapsible(item) && expandedCode === item.code && verbatimsIdFor(item)"
        class="mt-1 mb-2 rounded-md bg-muted/20 p-3">
        <ResultsVerbatimsList
          :question-id="verbatimsIdFor(item)"
          :recode-category-code="collapsible ? item.code : null"
          :with-sentiment="showSentiment"
        />
      </div>
    </div>
  </div>

  <!-- Vertical bars (column) -->
  <div v-else class="flex items-end gap-2 pt-4 pb-1" :class="showImages ? 'h-[248px]' : 'h-[200px]'">
    <div
      v-for="(item, itemIdx) in populations[0]?.data || []"
      :key="item.code || itemIdx"
      class="flex-1 flex flex-col items-center gap-1 h-full min-w-0 transition-all duration-200">
      <div class="flex items-end gap-0.5 flex-1 w-full">
        <div
          v-for="(pop, popIdx) in populations"
          :key="popIdx"
          class="flex-1 flex flex-col items-center h-full justify-end"
          :class="clickable ? 'cursor-pointer' : ''"
          @click="onPopBarClick($event, item, popIdx)">
          <Tooltip :delay-duration="0" :trigger-class="clickable ? '!cursor-pointer' : '!cursor-default'">
            <div class="flex flex-col items-center w-full" :style="{ height: '100%' }">
              <div class="flex-1" />
              <span class="text-3xs font-medium tabular-nums shrink-0 flex items-center gap-0.5 mb-0.5">
                <AnimatedPercent :value="(pop.data[itemIdx] || item).value" :delay="revealDelay(itemIdx)" />
                <span
                  v-if="showSignificance && sigIcon(pop.data[itemIdx] || item) === 'up'"
                  class="text-4xs text-status-success-strong"
                >▲</span>
                <span
                  v-if="showSignificance && sigIcon(pop.data[itemIdx] || item) === 'down'"
                  class="text-4xs text-status-error-strong"
                >▼</span>
              </span>
              <div
                class="w-full rounded-t bg-muted/30 overflow-hidden relative transition-all duration-300 starting:!h-0 starting:opacity-0"
                :style="[{ height: barWidth(pop.data[itemIdx] || item) }, revealDelayStyle(itemIdx)]">
                <div
                  v-if="showSentiment && (pop.data[itemIdx] || item).sentimentBreakdown"
                  class="absolute inset-0 rounded-t flex flex-col-reverse overflow-hidden">
                  <div class="w-full" :style="{ height: (pop.data[itemIdx] || item).sentimentBreakdown.negative * 100 + '%', backgroundColor: SENTIMENT_COLORS.negative }" />
                  <div class="w-full" :style="{ height: (pop.data[itemIdx] || item).sentimentBreakdown.neutral * 100 + '%', backgroundColor: SENTIMENT_COLORS.neutral }" />
                  <div class="w-full" :style="{ height: (pop.data[itemIdx] || item).sentimentBreakdown.positive * 100 + '%', backgroundColor: SENTIMENT_COLORS.positive }" />
                </div>
                <div
                  v-else
                  class="absolute inset-0 rounded-t transition-all duration-300"
                  :style="{ backgroundColor: (pop.data[itemIdx] || item).color || pop.color, opacity: (pop.data[itemIdx] || item).isSubtotal ? 0.5 : 1 }" />
              </div>
            </div>
            <template #content>
              <BarChartTooltip
                :item="item"
                :item-idx="itemIdx"
                :populations="populations"
                :show-significance="showSignificance"
                :show-sentiment="showSentiment"
                :clickable="false"
                @bar-click="handleBarClick($event)"
              />
            </template>
          </Tooltip>
        </div>
      </div>
      <Tooltip v-if="showImages && item.image" :delay-duration="0" trigger-class="shrink-0" content-class="bg-transparent p-0 shadow-none">
        <img
          :src="item.image"
          :alt="item.label"
          class="h-10 w-10 rounded object-cover cursor-zoom-in"
          @click.stop="previewImage = item.image" />
        <template #content>
          <img :src="item.image" :alt="item.label" class="max-h-60 max-w-60 rounded-md border bg-background object-contain shadow-lg" />
        </template>
      </Tooltip>
      <span
        class="text-3xs font-medium truncate max-w-full text-center shrink-0"
        v-html="renderMarkdownInline(item.label)" />
    </div>
  </div>

  <!-- Sentiment legend -->
  <div v-if="showSentiment" class="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
    <span
      v-for="segment in SENTIMENT_SEGMENTS"
      :key="segment.key"
      class="flex items-center gap-1.5">
      <span
        class="inline-block h-2 w-2 rounded-full"
        :style="{ backgroundColor: segment.color }" />
      {{ segment.label }}
    </span>
  </div>
  <ImageLightbox
    v-if="showImages"
    :src="previewImage"
    :open="!!previewImage"
    @update:open="(value) => { if (!value) previewImage = null; }" />
</template>
